(() => {
  if (!window.limencyPillowPreviewSettings) return;
  const settings = window.limencyPillowPreviewSettings || {};
  const BG_TOLERANCE = Number(settings.backgroundTolerance ?? 8);
  const ROUNDING = Number(settings.smoothing ?? 11);
  const FABRIC_EDGE = Number(settings.fabricBorder ?? 8);
  let overlay, last = 0, scheduled = false;
  const isOneSide = () => document.querySelector('[data-bonaper-field="print-sides"] select')?.value === 'one-side';
  const webglSupported = (() => {
    try {
      const canvas = document.createElement('canvas');
      return !!(canvas.getContext('webgl2') || canvas.getContext('webgl'));
    } catch {
      return false;
    }
  })();
  const can3d = () => webglSupported && document.documentElement.dataset.bonaperPillow3d !== 'unsupported';

  // The 3D model replaces the flat front/back preview once a photo is ready;
  // the flat preview stays as the fallback when WebGL is unavailable.
  function setView(preview, want3d) {
    const view = want3d ? 'true' : 'false';
    if (preview.dataset.bonaperPillowView === view) return;
    preview.dataset.bonaperPillowView = view;
    last = 0;
    if (want3d) setFace('front');
    preview.dispatchEvent(new CustomEvent('bonaper:pillow-view', { bubbles: true, detail: { view } }));
  }

  // Front/Back switch over the 3D model; the 3D script turns the pillow to
  // the requested face and sways gently around it.
  let faceSwitch;
  function setFace(face) {
    faceSwitch?.querySelectorAll('button').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.face === face));
    });
    document.dispatchEvent(new CustomEvent('bonaper:pillow-face', { detail: { face } }));
  }
  function syncFaceSwitch(frame, preview, photos) {
    if (!faceSwitch) {
      faceSwitch = document.createElement('div');
      faceSwitch.className = 'bonaper-pillow-face';
      faceSwitch.setAttribute('role', 'group');
      faceSwitch.setAttribute('aria-label', 'Pillow side');
      for (const [face, label] of [['front', 'Front'], ['back', 'Back']]) {
        const button = Object.assign(document.createElement('button'), { type: 'button', textContent: label });
        button.dataset.face = face;
        button.setAttribute('aria-pressed', String(face === 'front'));
        button.addEventListener('click', () => setFace(face));
        faceSwitch.append(button);
      }
    }
    if (faceSwitch.parentElement !== frame) frame.append(faceSwitch);
    faceSwitch.hidden = photos || preview.dataset.bonaperPillowView !== 'true' || !can3d();
  }
  const makeCanvas = (w, h) => Object.assign(document.createElement('canvas'), { width: w, height: h });
  function drawBackdrop(ctx, w, h) {
    const gradient = ctx.createRadialGradient(w * .5, h * .4, 0, w * .5, h * .4, Math.max(w, h) * .7);
    gradient.addColorStop(0, '#F7F8FA'); gradient.addColorStop(1, '#E3E7EE');
    ctx.fillStyle = gradient; ctx.fillRect(0, 0, w, h);
  }

  function setup(base) {
    const frame = base.parentElement;
    const preview = frame?.closest('[data-bonaper-preview]');
    if (!frame || !preview) return false;
    const viewSwitch = preview.querySelector('[data-bonaper-view-switch]');
    if (viewSwitch) viewSwitch.style.height = '56px';
    if (!overlay || overlay.parentElement !== frame) {
      overlay?.remove();
      overlay = makeCanvas(base.width, base.height);
      overlay.className = 'bonaper-pillow-angle-canvas';
      overlay.style.cssText = 'position:absolute;z-index:3;pointer-events:none;';
      overlay.setAttribute('aria-hidden', 'true');
      frame.append(overlay);
      last = 0;
    }
    if (overlay.width !== base.width || overlay.height !== base.height) {
      overlay.width = base.width; overlay.height = base.height; last = 0;
    }
    const b = base.getBoundingClientRect(), p = frame.getBoundingClientRect();
    overlay.style.left = `${b.left - p.left}px`;
    overlay.style.top = `${b.top - p.top}px`;
    overlay.style.width = `${b.width}px`;
    overlay.style.height = `${b.height}px`;
    const photos = viewSwitch?.dataset.bonaperView === 'photos';
    overlay.hidden = !!photos;
    syncFaceSwitch(frame, preview, !!photos);
    return !photos;
  }

  function extract(pixels, w, h, index) {
    const s = w / 1200, x = Math.round((index ? 635 : 65) * s), y = Math.round(96 * s);
    const pw = Math.round(500 * s), ph = Math.round(710 * s);
    const bg = [pixels[0], pixels[1], pixels[2]];
    const photo = makeCanvas(pw, ph), ctx = photo.getContext('2d');
    const data = ctx.createImageData(pw, ph);
    let count = 0;
    for (let py = 0; py < ph; py++) for (let px = 0; px < pw; px++) {
      const from = ((y + py) * w + x + px) * 4, to = (py * pw + px) * 4;
      const diff = Math.max(Math.abs(pixels[from] - bg[0]), Math.abs(pixels[from + 1] - bg[1]), Math.abs(pixels[from + 2] - bg[2]));
      if (diff <= BG_TOLERANCE || pixels[from + 3] < 100) continue;
      count++;
      data.data[to] = pixels[from]; data.data[to + 1] = pixels[from + 1];
      data.data[to + 2] = pixels[from + 2]; data.data[to + 3] = 255;
    }
    if (count < pw * ph * .01) return null;
    ctx.putImageData(data, 0, 0);
    const mask = makeCanvas(pw, ph), m = mask.getContext('2d');
    m.filter = `blur(${ROUNDING * s}px)`;
    m.drawImage(photo, 0, 0);
    m.filter = 'none';
    const smooth = m.getImageData(0, 0, pw, ph);
    for (let p = 0; p < pw * ph; p++) {
      const i = p * 4;
      smooth.data[i] = smooth.data[i + 1] = smooth.data[i + 2] = 255;
      smooth.data[i + 3] = smooth.data[i + 3] > 70 ? 255 : 0;
    }
    // The colour key can mistake pale fur for the cream preview background.
    // Fill enclosed gaps in the silhouette, then keep the original photo colour.
    const outside = new Uint8Array(pw * ph), queue = new Int32Array(pw * ph);
    let head = 0, tail = 0;
    const add = (p) => { if (!outside[p] && smooth.data[p * 4 + 3] === 0) { outside[p] = 1; queue[tail++] = p; } };
    for (let x = 0; x < pw; x++) { add(x); add((ph - 1) * pw + x); }
    for (let y = 0; y < ph; y++) { add(y * pw); add(y * pw + pw - 1); }
    while (head < tail) {
      const p = queue[head++], x = p % pw, y = (p / pw) | 0;
      if (x) add(p - 1); if (x < pw - 1) add(p + 1);
      if (y) add(p - pw); if (y < ph - 1) add(p + pw);
    }
    for (let p = 0; p < pw * ph; p++) if (!outside[p]) smooth.data[p * 4 + 3] = 255;
    m.putImageData(smooth, 0, 0);
    const original = makeCanvas(pw, ph), originalCtx = original.getContext('2d');
    const originalData = originalCtx.createImageData(pw, ph);
    for (let py = 0; py < ph; py++) for (let px = 0; px < pw; px++) {
      const from = ((y + py) * w + x + px) * 4, to = (py * pw + px) * 4;
      originalData.data[to] = pixels[from]; originalData.data[to + 1] = pixels[from + 1];
      originalData.data[to + 2] = pixels[from + 2]; originalData.data[to + 3] = 255;
    }
    originalCtx.putImageData(originalData, 0, 0);
    originalCtx.globalCompositeOperation = 'destination-in'; originalCtx.drawImage(mask, 0, 0);
    const shell = makeCanvas(pw, ph), sc = shell.getContext('2d');
    // The reference pillow has a narrow fabric seam, not a broad white halo.
    const r = FABRIC_EDGE * s;
    for (let n = 0; n < 48; n++) {
      const a = n * Math.PI / 24;
      sc.drawImage(mask, Math.cos(a) * r, Math.sin(a) * r);
    }
    sc.globalCompositeOperation = 'source-in';
    sc.fillStyle = '#fffdf9'; sc.fillRect(0, 0, pw, ph);
    const front = makeCanvas(pw, ph), fc = front.getContext('2d');
    fc.drawImage(shell, 0, 0); fc.drawImage(original, 0, 0);
    return { front, shell };
  }

  // The back panel of a one-sided pillow is unprinted fabric in the same shape.
  function drawPlainBack(ctx, shell, s) {
    const fabric = makeCanvas(shell.width, shell.height), fctx = fabric.getContext('2d');
    fctx.drawImage(shell, 0, 0);
    fctx.globalCompositeOperation = 'source-in';
    const gradient = fctx.createLinearGradient(0, 0, fabric.width, fabric.height);
    gradient.addColorStop(0, '#fffdf9'); gradient.addColorStop(1, '#ece7df');
    fctx.fillStyle = gradient; fctx.fillRect(0, 0, fabric.width, fabric.height);
    fctx.globalCompositeOperation = 'source-atop';
    fctx.fillStyle = 'rgba(120, 104, 88, .05)';
    for (let y = 0; y < fabric.height; y += 4 * s) fctx.fillRect(0, y, fabric.width, Math.max(1, s));
    ctx.drawImage(fabric, 635 * s, 96 * s);
    ctx.save();
    ctx.textAlign = 'center';
    ctx.fillStyle = '#6b6259';
    ctx.font = `600 ${30 * s}px Arial, sans-serif`;
    ctx.fillText('Plain fabric back', 885 * s, 870 * s);
    ctx.font = `${24 * s}px Arial, sans-serif`;
    ctx.fillText('No photo printed on this side', 885 * s, 910 * s);
    ctx.restore();
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => { scheduled = false; update(); });
  }

  function update() {
    const base = document.querySelector('[data-bonaper-canvas]');
    if (!base || base.width < 100 || !setup(base)) return;
    try {
      const w = base.width, h = base.height;
      const image = base.getContext('2d', { willReadFrequently: true }).getImageData(0, 0, w, h);
      const pixels = image.data;
      const oneSide = isOneSide();
      const thumb = document.querySelector('.bonaper-field[data-bonaper-field="pet-photo"] .bonaper-photo__thumb:not([hidden])');
      const hasPhoto = !!(thumb instanceof HTMLImageElement && thumb.complete && thumb.naturalWidth);
      setView(base.closest('[data-bonaper-preview]'), hasPhoto && can3d());
      syncFaceSwitch(base.parentElement, base.closest('[data-bonaper-preview]'), false);
      let hash = 2166136261 ^ (oneSide ? 1039 : 0);
      for (let py = 0; py < h; py += 13) for (let px = 0; px < w; px += 13) {
        const i = (py * w + px) * 4;
        hash = Math.imul(hash ^ pixels[i] ^ (pixels[i + 1] << 8) ^ (pixels[i + 2] << 16), 16777619);
      }
      if (hash === last) return;
      const result = hasPhoto ? extract(pixels, w, h, 0) : null;
      const back = hasPhoto ? extract(pixels, w, h, 1) : null;
      const ctx = overlay.getContext('2d');
      ctx.clearRect(0, 0, w, h);
      // Always paint the flat preview: it sits under the 3D view while the
      // model builds and is what customers see when WebGL is unavailable.
      {
        const bg = [pixels[0], pixels[1], pixels[2]];
        for (let p = 0; p < w * h; p++) {
          const i = p * 4;
          const diff = Math.max(Math.abs(pixels[i] - bg[0]), Math.abs(pixels[i + 1] - bg[1]), Math.abs(pixels[i + 2] - bg[2]));
          if (diff < BG_TOLERANCE) pixels[i + 3] = 0;
        }
        ctx.putImageData(image, 0, 0);
        ctx.globalCompositeOperation = 'destination-over';
        drawBackdrop(ctx, w, h);
        ctx.globalCompositeOperation = 'source-over';
        if (result) {
          const s = w / 1200;
          ctx.save();
          ctx.shadowColor = 'rgba(0, 31, 29, .3)'; ctx.shadowBlur = 18 * s; ctx.shadowOffsetY = 10 * s;
          ctx.drawImage(result.shell, 65 * s, 96 * s);
          if (back || oneSide) ctx.drawImage(back?.shell || result.shell, 635 * s, 96 * s);
          ctx.restore();
          ctx.drawImage(result.front, 65 * s, 96 * s);
          if (oneSide) drawPlainBack(ctx, result.shell, s);
          else if (back) ctx.drawImage(back.front, 635 * s, 96 * s);
        }
      }
      last = hash;
    } catch { overlay.hidden = true; last = 0; }
  }
  new MutationObserver(schedule).observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['src', 'hidden', 'data-bonaper-view'] });
  document.addEventListener('change', (event) => {
    if (event.target?.closest?.('[data-bonaper-field="print-sides"]')) { last = 0; schedule(); }
  });
  document.addEventListener('load', schedule, true);
  window.addEventListener('resize', schedule);
  // BonaPer repaints its canvas without DOM mutations, so keep a slow poll as a safety net.
  setInterval(update, 1000);
  update();
})();
